/**
 * The terms a Trade Feed search highlights, shared by everything that has to
 * agree about them.
 *
 * It exists because two separate places now need the same answer: the
 * highlighter marks the terms, and the snippet picker chooses which line of a
 * long message to show BECAUSE it contains one. If those two disagreed the card
 * would show a line with nothing marked on it, which reads as a bug in the
 * search rather than a difference of opinion between two regexes.
 *
 * Terms are sorted LONGEST FIRST so an overlapping pair marks the longer match:
 * searching "126 126500" should mark "126500" whole, not leave a "500" tail
 * outside the mark.
 *
 * Single characters are dropped. One character matches nearly everything and
 * turns a price list into a wall of marks, which is noise rather than an answer.
 */
export default function searchTerms(query) {
  return String(query ?? "")
    .trim()
    .split(/\s+/)
    .filter((term) => term.length > 1)
    .sort((a, b) => b.length - a.length);
}

/**
 * Regex-escaped, because these are dealer references and not patterns.
 * "7118/1300R" is harmless; "(new)" is an unbalanced group that throws.
 */
export const escapeTerm = (term) => term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * One alternation over every term, or null when there is nothing to match.
 *
 * Pass "gi" to split on it, "i" to test or locate — a /g regex carries
 * lastIndex between calls, so reusing one for `.test()` silently skips every
 * other match.
 */
export function termsRegex(terms, flags = "gi") {
  if (!Array.isArray(terms) || terms.length === 0) return null;

  try {
    return new RegExp(`(${terms.map(escapeTerm).join("|")})`, flags);
  } catch {
    // Belt and braces. The escaping above should make this unreachable, but a
    // throw here would take the whole grid down, and a missing highlight is
    // not worth an error boundary.
    return null;
  }
}
